/**
 * Best Time to Run
 *
 * Clean, professional air quality forecast for runners.
 * Recommends optimal time to run based on AQI, temperature, UV.
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useRunningStore } from '@/stores/useRunningStore';
import { useLocationStore } from '@/stores';
import type { BestRunningTime } from '@/types/running';

interface BestTimeToRunProps {
  isOpen: boolean;
  onClose: () => void;
}

export function BestTimeToRun({ isOpen, onClose }: BestTimeToRunProps) {
  const bestRunningTimes = useRunningStore((s) => s.bestRunningTimes);
  const fetchBestRunningTimes = useRunningStore((s) => s.fetchBestRunningTimes);
  const currentPosition = useLocationStore((s) => s.currentPosition);
  const [selectedSlot, setSelectedSlot] = useState<BestRunningTime | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    const lat = currentPosition?.lat ?? 3.139;
    const lng = currentPosition?.lng ?? 101.6869;
    fetchBestRunningTimes(lat, lng);
  }, [isOpen, currentPosition, fetchBestRunningTimes]);

  useEffect(() => {
    if (bestRunningTimes.length > 0) {
      const best = bestRunningTimes.reduce((a, b) =>
        a.recommendation_score > b.recommendation_score ? a : b
      );
      setSelectedSlot(best);
    }
  }, [bestRunningTimes]);

  if (!isOpen) return null;

  const morningSlots = bestRunningTimes.filter(t => { const h = parseInt(t.time_slot); return h >= 5 && h < 10; });
  const middaySlots = bestRunningTimes.filter(t => { const h = parseInt(t.time_slot); return h >= 10 && h < 16; });
  const eveningSlots = bestRunningTimes.filter(t => { const h = parseInt(t.time_slot); return h >= 16 && h < 21; });
  const nightSlots = bestRunningTimes.filter(t => { const h = parseInt(t.time_slot); return h >= 21 || h < 5; });

  const bestSlot = bestRunningTimes.length > 0
    ? bestRunningTimes.reduce((a, b) => a.recommendation_score > b.recommendation_score ? a : b)
    : null;

  const formatHour = (slot: string) => {
    const h = parseInt(slot);
    if (h === 0) return '12 AM';
    if (h < 12) return `${h} AM`;
    if (h === 12) return '12 PM';
    return `${h - 12} PM`;
  };

  // Muted, desaturated palette — no sharp neon colors
  const qualityDot = (rec: string) => {
    const c = rec === 'excellent' ? 'bg-[#6b9f78]' : rec === 'good' ? 'bg-[#8a9ba8]' : rec === 'moderate' ? 'bg-[#b89f6a]' : 'bg-[#a87272]';
    return <span className={`inline-block w-1.5 h-1.5 rounded-full ${c}`} />;
  };

  // Professional wording: Optimal / Fair / Moderate / Poor
  const qualityText = (rec: string) => {
    if (rec === 'excellent') return 'Optimal';
    if (rec === 'good') return 'Fair';
    if (rec === 'moderate') return 'Moderate';
    return 'Poor';
  };

  const qualityLabel = (rec: string) => {
    const c = rec === 'excellent' ? 'text-[#6b9f78]' : rec === 'good' ? 'text-[#8a9ba8]' : rec === 'moderate' ? 'text-[#b89f6a]' : 'text-[#a87272]';
    return <span className={`text-xs font-medium ${c}`}>{qualityText(rec)}</span>;
  };

  const barColor = (rec: string) => {
    if (rec === 'excellent') return 'bg-[#6b9f78]/60';
    if (rec === 'good') return 'bg-[#8a9ba8]/50';
    if (rec === 'moderate') return 'bg-[#b89f6a]/50';
    return 'bg-[#a87272]/50';
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[65] bg-[#0f1115] overflow-y-auto"
      >
        <div className="min-h-screen max-w-lg mx-auto">
          {/* Header */}
          <div className="sticky top-0 z-10 bg-[#0f1115]/95 backdrop-blur-md px-5 py-4 flex items-center gap-4">
            <button onClick={onClose} className="p-1.5 -ml-1.5 text-slate-400 hover:text-white transition-colors">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-base font-semibold text-white">Best Time to Run</h1>
          </div>

          <div className="px-5 pb-10">
            {/* Recommended time — clean card */}
            {bestSlot && (
              <div className="mb-8">
                <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">Recommended window</p>
                <div className="bg-[#161920] rounded-xl p-5 border border-slate-800">
                  <div className="flex items-baseline justify-between mb-3">
                    <span className="text-3xl font-light text-white tracking-tight">{formatHour(bestSlot.time_slot)}</span>
                    {qualityLabel(bestSlot.recommendation)}
                  </div>
                  <div className="flex items-center gap-5 text-[13px] text-slate-500">
                    <span>AQI {bestSlot.predicted_aqi}</span>
                    <span className="text-slate-700">·</span>
                    <span>{bestSlot.temperature}°C</span>
                    <span className="text-slate-700">·</span>
                    <span>UV {bestSlot.uv_index}</span>
                  </div>
                  <p className="text-xs text-slate-600 mt-3">{bestSlot.reason}</p>
                </div>
              </div>
            )}

            {/* Forecast chart */}
            <div className="mb-8">
              <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-3 font-medium">24-hour forecast</p>
              <div className="bg-[#161920] rounded-xl p-4 border border-slate-800">
                <div className="flex gap-[3px] items-end h-20 mb-2">
                  {bestRunningTimes.slice(0, 24).map((slot, i) => {
                    const maxAqi = Math.max(...bestRunningTimes.map(s => s.predicted_aqi), 1);
                    const height = (slot.predicted_aqi / maxAqi) * 100;
                    const isSelected = selectedSlot?.time_slot === slot.time_slot;

                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedSlot(slot)}
                        className={`flex-1 rounded-[2px] transition-all ${barColor(slot.recommendation)} ${
                          isSelected ? 'opacity-100 ring-1 ring-white/40' : 'opacity-70 hover:opacity-90'
                        }`}
                        style={{ height: `${Math.max(6, height)}%` }}
                      />
                    );
                  })}
                </div>
                <div className="flex justify-between text-[10px] text-slate-600 font-mono">
                  <span>Now</span>
                  <span>+6h</span>
                  <span>+12h</span>
                  <span>+18h</span>
                  <span>+24h</span>
                </div>
              </div>
            </div>

            {/* Selected slot detail */}
            {selectedSlot && (
              <div className="mb-8">
                <div className="bg-[#161920] rounded-xl p-4 border border-slate-800">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm font-medium text-slate-300">{formatHour(selectedSlot.time_slot)}</span>
                    {qualityLabel(selectedSlot.recommendation)}
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    {[
                      { v: selectedSlot.predicted_aqi.toString(), l: 'AQI' },
                      { v: `${selectedSlot.temperature}°`, l: 'Temp' },
                      { v: `${selectedSlot.humidity}%`, l: 'Humidity' },
                      { v: selectedSlot.uv_index.toString(), l: 'UV' },
                    ].map((item, i) => (
                      <div key={i} className="text-center">
                        <div className="text-base font-normal text-slate-200 tabular-nums">{item.v}</div>
                        <div className="text-[10px] text-slate-600 mt-0.5">{item.l}</div>
                      </div>
                    ))}
                  </div>
                  <p className="text-xs text-slate-600 mt-4 pt-3 border-t border-slate-800/60">{selectedSlot.reason}</p>
                </div>
              </div>
            )}

            {/* Hourly breakdown */}
            {[
              { label: 'Morning', slots: morningSlots },
              { label: 'Midday', slots: middaySlots },
              { label: 'Evening', slots: eveningSlots },
              { label: 'Night', slots: nightSlots },
            ].map(({ label, slots }) => slots.length > 0 && (
              <div key={label} className="mb-6">
                <p className="text-[11px] text-slate-500 uppercase tracking-widest mb-2 font-medium">{label}</p>
                <div className="bg-[#161920] rounded-xl border border-slate-800 divide-y divide-slate-800/80">
                  {slots.map((slot, i) => {
                    const isActive = selectedSlot?.time_slot === slot.time_slot;
                    return (
                      <button
                        key={i}
                        onClick={() => setSelectedSlot(slot)}
                        className={`w-full flex items-center justify-between px-4 py-3 transition-colors ${
                          isActive ? 'bg-white/[0.03]' : 'hover:bg-white/[0.02]'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          {qualityDot(slot.recommendation)}
                          <span className="text-[13px] text-slate-300 tabular-nums w-14 text-left">{formatHour(slot.time_slot)}</span>
                          <span className="text-xs text-slate-600 tabular-nums">AQI {slot.predicted_aqi}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-xs text-slate-600 tabular-nums">{slot.temperature}°</span>
                          {qualityLabel(slot.recommendation)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Info footer */}
            <div className="mt-4 mb-8">
              <div className="bg-[#161920] rounded-xl p-4 border border-slate-800">
                <p className="text-xs text-slate-500 leading-relaxed">
                  AQI varies 30–50% between morning and peak hours. At running pace,
                  you breathe 7x more air than at rest — timing your run can halve your PM2.5 intake.
                </p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
